import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton.jsx";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";
import { useTenant } from "../../context/TenantContext";
import { useAuth } from "../../auth/AuthContext";
import { graphqlEndpoint } from "../../config";
import { UserRole } from "../../constants/userRoles.ts";

import { READ_USER_BY_ID } from "../../graph/users/queries";
import { UPDATE_USER } from "../../graph/users/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const tenantRoleOptions = [
  { value: UserRole.TENANT_ADMIN, label: "Tenant Admin" },
  { value: UserRole.TENANT_STAFF, label: "Tenant Staff" },
];

const validationSchema = yup.object().shape({
  firstName: yup.string().trim().required("Required"),

  lastName: yup.string().trim().required("Required"),

  email: yup
    .string()
    .email("Invalid email")
    .required("Required"),

  password: yup.string().notRequired(),

  verifiedEmail: yup.boolean().required(),

  role: yup
    .string()
    .oneOf([
      UserRole.TENANT_ADMIN,
      UserRole.TENANT_STAFF,
    ])
    .required("Required"),

  hourlyRate: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" ? null : value
    )
    .nullable()
    .min(0, "Hourly rate cannot be negative")
    .typeError("Hourly rate must be a valid number"),

  invitedBy: yup.string().nullable(),

  notes: yup.string().nullable(),
});

const EditUserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { tenantId, tenantName } = useTenant();
  const { user } = useAuth();
  const { setSnackbar } = useSnackbar();

  const [initialValues, setInitialValues] = useState(null);

  const isTenantAdmin =
    user?.role === UserRole.TENANT_ADMIN;

  const redirectPath =
    location.state?.redirectPath || "/manageUsers";

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId || !id) {
        return;
      }

      try {
        const { readUserByID } = await client.request(
          READ_USER_BY_ID,
          { id }
        );

        const allowedRoles = [
          UserRole.TENANT_ADMIN,
          UserRole.TENANT_STAFF,
        ];

        if (!allowedRoles.includes(readUserByID.role)) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "This page only supports tenant users",
          });

          navigate(redirectPath);
          return;
        }

        if (readUserByID.tenantID !== tenantId) {
          setSnackbar({
            open: true,
            severity: "error",
            message: "This user does not belong to this tenant",
          });

          navigate(redirectPath);
          return;
        }

        if (
          isTenantAdmin &&
          readUserByID.role !== UserRole.TENANT_STAFF
        ) {
          setSnackbar({
            open: true,
            severity: "error",
            message:
              "Tenant admins can only edit tenant staff users",
          });

          navigate(redirectPath);
          return;
        }

        setInitialValues({
          id: readUserByID.id,
          firstName: readUserByID.firstName || "",
          lastName: readUserByID.lastName || "",
          email: readUserByID.email || "",
          password: "",
          verifiedEmail:
            readUserByID.verifiedEmail ?? false,
          role:
            readUserByID.role ||
            UserRole.TENANT_STAFF,
          hourlyRate:
            readUserByID.hourlyRate ?? "",
          invitedBy:
            readUserByID.invitedBy || "",
          notes:
            readUserByID.notes || "",
          archivedAt:
            readUserByID.archivedAt || null,
        });
      } catch (err) {
        console.error("Error fetching user:", err);

        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load user",
        });
      }
    };

    fetchData();
  }, [
    id,
    tenantId,
    isTenantAdmin,
    navigate,
    redirectPath,
    setSnackbar,
  ]);

  const handleSubmit = async (values) => {
    if (!tenantId) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Tenant could not be resolved",
      });

      return;
    }

    const role = isTenantAdmin
      ? UserRole.TENANT_STAFF
      : values.role;

    try {
      const input = {
        id: values.id,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim().toLowerCase(),
        verifiedEmail: values.verifiedEmail,
        role,
        tenantID: tenantId,
        hourlyRate:
          values.hourlyRate === "" ||
          values.hourlyRate === null ||
          values.hourlyRate === undefined
            ? null
            : Number(values.hourlyRate),
        invitedBy:
          values.invitedBy.trim() || null,
        notes:
          values.notes.trim() || null,
      };

      if (values.password?.trim()) {
        input.password = values.password;
      }

      await client.request(UPDATE_USER, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "User updated successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error(
        "Update failed:",
        err?.response?.errors || err
      );

      const message =
        err?.response?.errors?.[0]?.message ||
        "Failed to update user";

      setSnackbar({
        open: true,
        severity: "error",
        message,
      });
    }
  };

  if (!tenantId) {
    return (
      <Box>
        <Header
          title="EDIT USER"
          subtitle="Tenant could not be resolved"
        />

        <Typography mt={3}>
          A tenant must be available before editing a user.
        </Typography>
      </Box>
    );
  }

  if (!initialValues) {
    return <div>Loading...</div>;
  }

  return (
    <Box>
      <Header
        title="EDIT USER"
        subtitle={`Edit ${initialValues.firstName} ${
          initialValues.lastName
        }${tenantName ? ` for ${tenantName}` : ""}`}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          touched,
          errors,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, 1fr)"
            >
              <TextField
                label="First Name"
                name="firstName"
                value={values.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.firstName &&
                    errors.firstName
                )}
                helperText={
                  touched.firstName &&
                  errors.firstName
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Last Name"
                name="lastName"
                value={values.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.lastName &&
                    errors.lastName
                )}
                helperText={
                  touched.lastName &&
                  errors.lastName
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Email"
                name="email"
                type="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.email && errors.email
                )}
                helperText={
                  touched.email && errors.email
                }
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Password (leave blank to keep existing)"
                name="password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                type="password"
                error={Boolean(
                  touched.password &&
                    errors.password
                )}
                helperText={
                  touched.password &&
                  errors.password
                }
                sx={{ gridColumn: "span 2" }}
              />

              {isTenantAdmin ? (
                <TextField
                  label="Role"
                  value="Tenant Staff"
                  disabled
                  helperText="Tenant admins cannot change staff roles"
                  sx={{ gridColumn: "span 2" }}
                />
              ) : (
                <FormControl
                  fullWidth
                  sx={{ gridColumn: "span 2" }}
                  error={Boolean(
                    touched.role && errors.role
                  )}
                >
                  <InputLabel>Role</InputLabel>

                  <Select
                    name="role"
                    value={values.role}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    label="Role"
                  >
                    {tenantRoleOptions.map(
                      (roleOption) => (
                        <MenuItem
                          key={roleOption.value}
                          value={roleOption.value}
                        >
                          {roleOption.label}
                        </MenuItem>
                      )
                    )}
                  </Select>

                  {touched.role && errors.role && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {errors.role}
                    </Typography>
                  )}
                </FormControl>
              )}

              <TextField
                label="Hourly Rate"
                name="hourlyRate"
                type="number"
                value={values.hourlyRate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(
                  touched.hourlyRate &&
                    errors.hourlyRate
                )}
                helperText={
                  touched.hourlyRate &&
                  errors.hourlyRate
                    ? errors.hourlyRate
                    : "Optional internal labour cost per hour"
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        £
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 0,
                    step: "0.01",
                  },
                }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Invited By"
                name="invitedBy"
                value={values.invitedBy}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText="Optional"
                sx={{ gridColumn: "span 2" }}
              />

              <MarkdownEditor
                value={values.notes}
                onChange={(nextValue) =>
                  setFieldValue("notes", nextValue)
                }
                label="Notes"
                minRows={14}
                error={Boolean(
                  touched.notes && errors.notes
                )}
                helperText={
                  touched.notes && errors.notes
                }
                sx={{ gridColumn: "span 4" }}
              />

              {values.archivedAt && (
                <Box sx={{ gridColumn: "span 4" }}>
                  <Typography
                    variant="body2"
                    color="warning.main"
                  >
                    This user is archived and should
                    generally not be edited.
                  </Typography>
                </Box>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={values.verifiedEmail}
                    onChange={handleChange}
                    name="verifiedEmail"
                  />
                }
                label="Verified Email"
              />
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              mt={3}
            >
              <ActionButton
                type="submit"
                tone="success"
                disabled={isSubmitting}
              >
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditUserForm;